'use client';

import React, { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import type { Presentation, Slide } from '../lib/types';
import { EditorCanvas } from './EditorCanvas';

interface PresenterModeProps {
    presentation: Presentation;
    initialSlideIndex: number;
    onClose: () => void;
}

export const PresenterMode: React.FC<PresenterModeProps> = ({
    presentation,
    initialSlideIndex,
    onClose
}) => {
    const [currentIndex, setCurrentIndex] = useState(initialSlideIndex);
    const currentSlide = presentation.slides[currentIndex];

    const goToPrevious = () => {
        if (currentIndex > 0) setCurrentIndex(currentIndex - 1);
    };

    const goToNext = () => {
        if (currentIndex < presentation.slides.length - 1) setCurrentIndex(currentIndex + 1);
    };

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'ArrowRight' || e.key === ' ' || e.key === 'PageDown') {
                goToNext();
            } else if (e.key === 'ArrowLeft' || e.key === 'Backspace' || e.key === 'PageUp') {
                goToPrevious();
            } else if (e.key === 'Escape') {
                onClose();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [currentIndex]);

    const [scale, setScale] = useState(1);

    useEffect(() => {
        const updateScale = () => {
            const padding = 40;
            const availableWidth = window.innerWidth - padding;
            const availableHeight = window.innerHeight - padding;
            const slideWidth = 1200;
            const slideHeight = 675;

            const scaleX = availableWidth / slideWidth;
            const scaleY = availableHeight / slideHeight;
            const newScale = Math.min(scaleX, scaleY);
            setScale(newScale);
        };

        updateScale();
        window.addEventListener('resize', updateScale);
        return () => window.removeEventListener('resize', updateScale);
    }, []);

    return (
        <div className="fixed inset-0 z-[99999] bg-[#0A0A0A] flex flex-col items-center justify-center overflow-hidden">
            {/* Header / Controls */}
            <div className="absolute top-0 left-0 right-0 p-6 flex justify-between items-center opacity-0 hover:opacity-100 transition-opacity bg-gradient-to-b from-black/60 to-transparent z-[100] pointer-events-none">
                <div className="text-white/80 font-bold ml-4 pointer-events-auto">
                    Slide {currentIndex + 1} of {presentation.slides.length}
                </div>
                <button
                    onClick={onClose}
                    className="p-3 bg-white/10 hover:bg-white/20 text-white rounded-full transition-all pointer-events-auto"
                >
                    <X size={24} />
                </button>
            </div>

            {/* Slide Container */}
            <div className="w-full h-full flex items-center justify-center">
                <div
                    className="relative shadow-2xl shadow-black/80 bg-white"
                    style={{
                        width: '1200px',
                        height: '675px',
                        transform: `scale(${scale})`,
                        transformOrigin: 'center center'
                    }}
                >
                    <EditorCanvas
                        slide={currentSlide}
                        onElementUpdate={() => { }} // No editing in presenter mode
                        onSelect={() => { }} // No selection in presenter mode
                        uiScale={0.75} // Base width of 1200px / 1600px
                        onSlashCommand={() => { }} // No slash commands in presenter mode
                        selectedElementId={null}
                        onAiGenerateClick={() => { }}
                        onAiGenerateInfographicClick={() => { }}
                        onUploadImageClick={() => { }}
                        onDeleteElement={() => { }}
                    />
                </div>
            </div>

            {/* Navigation Controls */}
            <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex items-center gap-6 opacity-0 hover:opacity-100 transition-opacity bg-black/40 backdrop-blur-md px-6 py-3 rounded-full border border-white/10 pointer-events-auto">
                <button
                    disabled={currentIndex === 0}
                    onClick={goToPrevious}
                    className="p-2 text-white/50 hover:text-white disabled:opacity-20 transition-colors"
                >
                    <ChevronLeft size={32} />
                </button>
                <div className="h-6 w-[1px] bg-white/10" />
                <button
                    disabled={currentIndex === presentation.slides.length - 1}
                    onClick={goToNext}
                    className="p-2 text-white/50 hover:text-white disabled:opacity-20 transition-colors"
                >
                    <ChevronRight size={32} />
                </button>
            </div>
        </div>
    );
};
